import CheckGroup from 'components/elements/form/CheckGroup'
import TextAreaGroup from 'components/elements/form/TextAreaGroup'
import InformationModal from 'components/elements/modal/InformationModal'
import Text from 'components/elements/texts/Text'
import RequestTemplate from 'components/templates/RequestTemplate'
import { DEFAULT_DISABLED_BUTTON_LABEL } from 'constants/label'
import { ERROR } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import {
  RA_LCAR_COST_SC_WISHES,
  RA_LODGING_SC_WISHES,
  RA_TRANSFER_SC_WISHES,
} from 'constants/qnState'
import { EditingItemContext } from 'contexts/EditingItemContext'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { QnStatesProps } from 'states/QnState'
import styled from 'styled-components'
import { StatesProps, useCheckState, useTextAreaState } from 'utils/commonHooks'
import {
  containsIncompatibleChar,
  createProps,
  isEmptyCheck,
  isSelectedCheck,
} from 'utils/commonUtils'
import { useDidMount } from 'utils/lifecycleHooks'
import { hasRaLcarCost, hasRaLodgingTransfer } from 'utils/specialContract'
import { StatusProps } from 'utils/status'
import { useProgressPoint } from '../../utils/commonHooks'

/**
 * RAK018: 特約利用選択画面
 */
const SpecialContract: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editingItem, setEditingItem] = useContext(EditingItemContext)
  const { state, setState } = createProps(props, editingItem)

  const [raTransferScWishes, setRaTransferScWishes] = useCheckState(
    state.raTransferScWishes
  )
  const [raLodgingScWishes, setRaLodgingScWishes] = useCheckState(
    state.raLodgingScWishes
  )
  const [raLcarCostScWishes, setRaLcarCostScWishes] = useCheckState(
    state.raLcarCostScWishes
  )
  const [
    raLcarCostScSupplWishes,
    setRaLcarCostScSupplWishes,
  ] = useTextAreaState(state.raLcarCostScSupplWishes)

  const [raTransferModal1, setRaTransferModal1] = useState(false)
  const [raLodgingModal1, setRaLodgingModal1] = useState(false)
  const [raLcarCostModal1, setRaLcarCostModal1] = useState(false)
  const [raLcarCostModal2, setRaLcarCostModal2] = useState(false)

  const [isDisabled, setIsDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const closeModal = useCallback(
    (setModal: React.Dispatch<React.SetStateAction<boolean>>) => {
      setModal(false)
    },
    []
  )

  const forwardAction = useCallback((): StatusProps => {
    return setState({
      ...state,
    })
  }, [state])

  const forwardTo = state.confirmationChangeType
    ? PAGE_PATH.CONFIRMATION
    : PAGE_PATH.SERVICE_OTHER_EMPTY

  useEffect(() => {
    const updatedState = {
      raTransferScWishes,
      raLodgingScWishes,
      raLcarCostScWishes,
      raLcarCostScSupplWishes,
    }

    setEditingItem({
      ...editingItem,
      ...updatedState,
    })

    // バリデーション
    // 表示中の項目で一つも選択していない項目があれば必須項目NGとする
    const isRequiredChecked = ((): boolean => {
      if (hasRaLodgingTransfer(state) && hasRaLcarCost(state)) {
        // RA宿泊・移動・代車費用特約
        return (
          isEmptyCheck(raTransferScWishes) ||
          isEmptyCheck(raLodgingScWishes) ||
          isEmptyCheck(raLcarCostScWishes)
        )
      } else if (hasRaLodgingTransfer(state)) {
        // RA宿泊・移動特約
        return (
          isEmptyCheck(raTransferScWishes) || isEmptyCheck(raLodgingScWishes)
        )
      } else if (hasRaLcarCost(state)) {
        // RA代車費用特約
        return isEmptyCheck(raLcarCostScWishes)
      }
      return false
    })()

    if (isRequiredChecked) {
      // 必須のチェックなし
      setErrorMessage(DEFAULT_DISABLED_BUTTON_LABEL)
      setIsDisabled(true)
    } else if (
      hasRaLcarCost(state) &&
      containsIncompatibleChar(raLcarCostScSupplWishes.text.inputText)
    ) {
      // 補足内容に禁止文字
      setErrorMessage(ERROR.CONTAINS_SURROGATE_PAIR)
      setIsDisabled(true)
    } else {
      setErrorMessage('')
      setIsDisabled(false)
    }
  }, [
    raTransferScWishes,
    raLodgingScWishes,
    raLcarCostScWishes,
    raLcarCostScSupplWishes,
  ])

  // 移動費用特約モーダル表示
  useEffect(() => {
    if (
      isSelectedCheck(raTransferScWishes, RA_TRANSFER_SC_WISHES.YES) ||
      isSelectedCheck(raTransferScWishes, RA_TRANSFER_SC_WISHES.THINKING)
    ) {
      setRaTransferModal1(true)
    }
  }, [raTransferScWishes])

  // 宿泊費用特約モーダル表示
  useEffect(() => {
    if (
      isSelectedCheck(raLodgingScWishes, RA_LODGING_SC_WISHES.YES) ||
      isSelectedCheck(raLodgingScWishes, RA_LODGING_SC_WISHES.THINKING)
    ) {
      setRaLodgingModal1(true)
    }
  }, [raLodgingScWishes])

  // 代車費用特約モーダル表示
  useEffect(() => {
    if (isSelectedCheck(raLcarCostScWishes, RA_LCAR_COST_SC_WISHES.YES)) {
      setRaLcarCostModal1(true)
    }
    if (
      isSelectedCheck(
        raLcarCostScWishes,
        RA_LCAR_COST_SC_WISHES.ALREADY_ARRANGED
      )
    ) {
      setRaLcarCostModal2(true)
    }
  }, [raLcarCostScWishes])

  useDidMount(() => {
    setRaTransferModal1(false)
    setRaLodgingModal1(false)
    setRaLcarCostModal1(false)
    setRaLcarCostModal2(false)
  })

  return (
    <RequestTemplate
      title={'このあとの移動について'}
      description={
        hasRaLodgingTransfer(state)
          ? 'お客さまの車両には、本トラブルによりお車をお運びした場合に限り今後の移動や臨時に宿泊せざるを得ない場合の費用を補償する特約がついています。'
          : ''
      }
      showContact
      progress={useProgressPoint()}
      content={
        <React.Fragment>
          {hasRaLodgingTransfer(state) && (
            <React.Fragment>
              <CheckGroup
                {...raTransferScWishes}
                setChecks={setRaTransferScWishes}
              />
              <NoteText>
                レッカー車への同乗は安全上ご遠慮いただいております。
              </NoteText>
              <CheckGroup
                {...raLodgingScWishes}
                setChecks={setRaLodgingScWishes}
              />
              <NoteText>もともと予定されていた宿泊は対象外です。</NoteText>
            </React.Fragment>
          )}
          {hasRaLcarCost(state) && (
            <React.Fragment>
              <CheckGroup
                {...raLcarCostScWishes}
                setChecks={setRaLcarCostScWishes}
              />
              <TextAreaGroup
                {...raLcarCostScSupplWishes}
                setText={setRaLcarCostScSupplWishes}
              />
            </React.Fragment>
          )}
          <InformationModal
            on={raTransferModal1}
            content={
              '移動費用の限度額は１名につき２万円です。タクシー・レンタカーの場合は、１台につき２万円が限度です。費用精算については、担当部署よりご連絡、またはご請求に必要な書類をお送りします。'
            }
            closeLabel="とじる"
            closeFunction={(): void => {
              closeModal(setRaTransferModal1)
            }}
            greyBack
            scrollLock
            greyBackClose
          />
          <InformationModal
            on={raLodgingModal1}
            content={
              '宿泊費用の限度額は、１名につき１万円です。費用精算については、担当部署よりご連絡、またはご請求に必要な書類をお送り致します。'
            }
            closeLabel="とじる"
            closeFunction={(): void => {
              closeModal(setRaLodgingModal1)
            }}
            greyBack
            scrollLock
            greyBackClose
          />
          <InformationModal
            on={raLcarCostModal1}
            content={'担当部署から順番にご連絡差し上げます。'}
            closeLabel="とじる"
            closeFunction={(): void => {
              closeModal(setRaLcarCostModal1)
            }}
            greyBack
            scrollLock
            greyBackClose
          />
          <InformationModal
            on={raLcarCostModal2}
            content={
              '費用精算については、改めて担当部署よりご連絡差し上げます。'
            }
            closeLabel="とじる"
            closeFunction={(): void => {
              closeModal(setRaLcarCostModal2)
            }}
            greyBack
            scrollLock
            greyBackClose
          />
        </React.Fragment>
      }
      error={errorMessage}
      forwardTo={forwardTo}
      forwardAction={forwardAction}
      showBack
      isDisabled={isDisabled}
      disabledLabel={DEFAULT_DISABLED_BUTTON_LABEL}
      notClearEditingItem
    />
  )
}

const NoteText = styled(Text)`
  margin: 8px 16px 0;
  font-weight: bold;
  font-size: 13px;
  &::before {
    margin-right: 8px;
    content: '※';
  }
`

export default SpecialContract
